// src/api/features/bookings-admin/mutations.ts

import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  adminUpdateBookingStatus,
  adminEditBooking,
  adminReplaceTravelers,
  adminSetPaymentLink,
  adminGeneratePaymentLink,
  adminGenerateVoucher, // ✅ ADD
  adminDeleteBooking,
  adminBulkDeleteBookings,
  adminUpsertTravelPlan,
  adminDeleteTravelPlan,
} from "./api";

import type {
  AdminBookingResult,
  PaginatedAdminBookings,
  AdminUpdateStatusBody,
  AdminUpdatePaxBody,
  AdminReplaceTravelersBody,
  AdminSetPaymentLinkBody,
  AdminGeneratePaymentLinkBody,
  AdminGeneratePaymentLinkResult,
  AdminTravelPlanResult,
  AdminUpsertTravelPlanBody,
  AdminGenerateVoucherBody, // ✅ ADD
  AdminGenerateVoucherResult, // ✅ ADD
  AdminBulkDeleteResult,
} from "./types";

import { AdminBookingKeys } from "./queries";

/* =====================================
 * Local helper types (for mutation context)
 * ===================================== */

type AdminBookingsListSnapshot = {
  key: readonly unknown[];
  data: PaginatedAdminBookings | undefined;
};

type UpdateAdminBookingContext = {
  prevDetail?: AdminBookingResult;
  prevLists?: AdminBookingsListSnapshot[];
};

type DeleteAdminBookingContext = {
  prevLists?: AdminBookingsListSnapshot[];
};

type UpdateAdminTravelPlanContext = {
  prevPlan?: AdminTravelPlanResult;
};

type DeleteAdminTravelPlanContext = {
  prevPlan?: AdminTravelPlanResult;
};

function patchListsBooking(
  qc: ReturnType<typeof useQueryClient>,
  id: number | string,
  patch: (b: AdminBookingResult) => AdminBookingResult
) {
  const queries = qc.getQueryCache().findAll({ queryKey: AdminBookingKeys.all });

  for (const q of queries) {
    const list = q.state.data as PaginatedAdminBookings | undefined;
    if (!list?.items) continue;

    qc.setQueryData<PaginatedAdminBookings>(q.queryKey, {
      ...list,
      items: list.items.map((b) => (String(b.id) === String(id) ? patch(b) : b)),
    });
  }
}

/* =====================================
 * Admin Booking mutations
 * ===================================== */

/** 🔹 Admin: Update booking status */
export function useAdminUpdateBookingStatusMutation() {
  const qc = useQueryClient();

  return useMutation<
    AdminBookingResult,
    unknown,
    { id: number | string; data: AdminUpdateStatusBody },
    UpdateAdminBookingContext
  >({
    mutationFn: ({ id, data }) => adminUpdateBookingStatus(id, data),

    async onMutate({ id, data }) {
      await Promise.all([
        qc.cancelQueries({ queryKey: AdminBookingKeys.byId(id) }),
        qc.cancelQueries({ queryKey: AdminBookingKeys.all }),
      ]);

      const prevDetail = qc.getQueryData<AdminBookingResult>(AdminBookingKeys.byId(id));

      const prevLists: AdminBookingsListSnapshot[] = qc
        .getQueryCache()
        .findAll({ queryKey: AdminBookingKeys.all })
        .map((q) => ({
          key: q.queryKey,
          data: q.state.data as PaginatedAdminBookings | undefined,
        }));

      if (prevDetail) {
        qc.setQueryData<AdminBookingResult>(AdminBookingKeys.byId(id), {
          ...prevDetail,
          status: data.status,
        });
      }

      patchListsBooking(qc, id, (b) => ({ ...b, status: data.status }));

      return { prevDetail, prevLists };
    },

    onError(_err, vars, ctx) {
      if (!ctx) return;

      if (ctx.prevDetail) {
        qc.setQueryData<AdminBookingResult>(AdminBookingKeys.byId(vars.id), ctx.prevDetail);
      }
      for (const snap of ctx.prevLists ?? []) {
        qc.setQueryData(snap.key, snap.data);
      }
    },

    onSuccess(updated, { id }) {
      qc.setQueryData<AdminBookingResult>(AdminBookingKeys.byId(id), updated);
      patchListsBooking(qc, id, () => updated);
    },

    onSettled(_res, _err, { id }) {
      qc.invalidateQueries({ queryKey: AdminBookingKeys.byId(id) });
      qc.invalidateQueries({ queryKey: AdminBookingKeys.all });
    },
  });
}

/** 🔹 Admin: Edit booking (basic changes / pax details) */
export function useAdminEditBookingMutation() {
  const qc = useQueryClient();

  return useMutation<
    AdminBookingResult,
    unknown,
    { id: number | string; data: AdminUpdatePaxBody },
    UpdateAdminBookingContext
  >({
    mutationFn: ({ id, data }) => adminEditBooking(id, data),

    async onMutate({ id, data }) {
      await Promise.all([
        qc.cancelQueries({ queryKey: AdminBookingKeys.byId(id) }),
        qc.cancelQueries({ queryKey: AdminBookingKeys.all }),
      ]);

      const prevDetail = qc.getQueryData<AdminBookingResult>(AdminBookingKeys.byId(id));

      const prevLists: AdminBookingsListSnapshot[] = qc
        .getQueryCache()
        .findAll({ queryKey: AdminBookingKeys.all })
        .map((q) => ({
          key: q.queryKey,
          data: q.state.data as PaginatedAdminBookings | undefined,
        }));

      if (prevDetail) {
        qc.setQueryData<AdminBookingResult>(AdminBookingKeys.byId(id), {
          ...prevDetail,
          paxRequested: data.paxRequested ?? prevDetail.paxRequested,
          occupancy: {
            ...(prevDetail.occupancy ?? {}),
            allocKey:
              data.allocKey !== undefined
                ? data.allocKey
                : prevDetail.occupancy?.allocKey ?? null,
          },
        });
      }

      patchListsBooking(qc, id, (b) => ({
        ...b,
        paxRequested: data.paxRequested ?? b.paxRequested,
        occupancy: {
          ...(b.occupancy ?? {}),
          allocKey: data.allocKey !== undefined ? data.allocKey : b.occupancy?.allocKey ?? null,
        },
      }));

      return { prevDetail, prevLists };
    },

    onError(_err, vars, ctx) {
      if (!ctx) return;

      if (ctx.prevDetail) {
        qc.setQueryData<AdminBookingResult>(AdminBookingKeys.byId(vars.id), ctx.prevDetail);
      }
      for (const snap of ctx.prevLists ?? []) {
        qc.setQueryData(snap.key, snap.data);
      }
    },

    onSuccess(updated, { id }) {
      qc.setQueryData<AdminBookingResult>(AdminBookingKeys.byId(id), updated);
      patchListsBooking(qc, id, () => updated);
    },

    onSettled(_res, _err, { id }) {
      qc.invalidateQueries({ queryKey: AdminBookingKeys.byId(id) });
      qc.invalidateQueries({ queryKey: AdminBookingKeys.all });
    },
  });
}

/** 🔹 Admin: Replace travelers */
export function useAdminReplaceTravelersMutation() {
  const qc = useQueryClient();

  return useMutation<
    AdminBookingResult,
    unknown,
    { id: number | string; data: AdminReplaceTravelersBody },
    UpdateAdminBookingContext
  >({
    mutationFn: ({ id, data }) => adminReplaceTravelers(id, data),

    async onMutate({ id }) {
      await Promise.all([
        qc.cancelQueries({ queryKey: AdminBookingKeys.byId(id) }),
        qc.cancelQueries({ queryKey: AdminBookingKeys.all }),
      ]);

      const prevDetail = qc.getQueryData<AdminBookingResult>(AdminBookingKeys.byId(id));

      const prevLists: AdminBookingsListSnapshot[] = qc
        .getQueryCache()
        .findAll({ queryKey: AdminBookingKeys.all })
        .map((q) => ({
          key: q.queryKey,
          data: q.state.data as PaginatedAdminBookings | undefined,
        }));

      return { prevDetail, prevLists };
    },

    onError(_err, vars, ctx) {
      if (!ctx) return;

      if (ctx.prevDetail) {
        qc.setQueryData<AdminBookingResult>(AdminBookingKeys.byId(vars.id), ctx.prevDetail);
      }
      for (const snap of ctx.prevLists ?? []) {
        qc.setQueryData(snap.key, snap.data);
      }
    },

    onSuccess(updated, { id }) {
      qc.setQueryData<AdminBookingResult>(AdminBookingKeys.byId(id), updated);
      patchListsBooking(qc, id, () => updated);
    },

    onSettled(_res, _err, { id }) {
      qc.invalidateQueries({ queryKey: AdminBookingKeys.byId(id) });
      qc.invalidateQueries({ queryKey: AdminBookingKeys.all });
    },
  });
}

/** 🔹 Admin: Set payment link (manual / paytabs / etc) */
export function useAdminSetPaymentLinkMutation() {
  const qc = useQueryClient();

  return useMutation<
    AdminBookingResult,
    unknown,
    { id: number | string; data: AdminSetPaymentLinkBody },
    UpdateAdminBookingContext
  >({
    mutationFn: ({ id, data }) => adminSetPaymentLink(id, data),

    async onMutate({ id, data }) {
      await Promise.all([
        qc.cancelQueries({ queryKey: AdminBookingKeys.byId(id) }),
        qc.cancelQueries({ queryKey: AdminBookingKeys.all }),
      ]);

      const prevDetail = qc.getQueryData<AdminBookingResult>(AdminBookingKeys.byId(id));

      const prevLists: AdminBookingsListSnapshot[] = qc
        .getQueryCache()
        .findAll({ queryKey: AdminBookingKeys.all })
        .map((q) => ({
          key: q.queryKey,
          data: q.state.data as PaginatedAdminBookings | undefined,
        }));

      if (prevDetail) {
        qc.setQueryData<AdminBookingResult>(AdminBookingKeys.byId(id), {
          ...prevDetail,
          paymentLink: {
            ...(prevDetail.paymentLink ?? {}),
            url: (data as any).paymentLink,
            provider:
              (data as any).provider !== undefined
                ? (data as any).provider
                : prevDetail.paymentLink?.provider ?? null,
            kind:
              (data as any).kind !== undefined
                ? (data as any).kind
                : prevDetail.paymentLink?.kind ?? null,
            expiresAt:
              (data as any).expiresAt !== undefined
                ? (data as any).expiresAt
                : prevDetail.paymentLink?.expiresAt ?? null,
            updatedAt: new Date().toISOString(),
          },
        });
      }

      return { prevDetail, prevLists };
    },

    onError(_err, vars, ctx) {
      if (!ctx) return;
      if (ctx.prevDetail) {
        qc.setQueryData<AdminBookingResult>(AdminBookingKeys.byId(vars.id), ctx.prevDetail);
      }
      for (const snap of ctx.prevLists ?? []) {
        qc.setQueryData(snap.key, snap.data);
      }
    },

    onSuccess(updated, { id }) {
      qc.setQueryData<AdminBookingResult>(AdminBookingKeys.byId(id), updated);
      patchListsBooking(qc, id, () => updated);
    },

    onSettled(_res, _err, { id }) {
      qc.invalidateQueries({ queryKey: AdminBookingKeys.byId(id) });
      qc.invalidateQueries({ queryKey: AdminBookingKeys.all });
    },
  });
}

/** 🔹 Admin: Generate HDFC payment link (main flow) */
export function useAdminGeneratePaymentLinkMutation() {
  const qc = useQueryClient();

  return useMutation<
    AdminGeneratePaymentLinkResult,
    unknown,
    { id: number | string; data: AdminGeneratePaymentLinkBody },
    UpdateAdminBookingContext
  >({
    mutationFn: ({ id, data }) => adminGeneratePaymentLink(id, data),

    async onMutate({ id }) {
      await Promise.all([
        qc.cancelQueries({ queryKey: AdminBookingKeys.byId(id) }),
        qc.cancelQueries({ queryKey: AdminBookingKeys.all }),
      ]);

      const prevDetail = qc.getQueryData<AdminBookingResult>(AdminBookingKeys.byId(id));

      const prevLists: AdminBookingsListSnapshot[] = qc
        .getQueryCache()
        .findAll({ queryKey: AdminBookingKeys.all })
        .map((q) => ({
          key: q.queryKey,
          data: q.state.data as PaginatedAdminBookings | undefined,
        }));

      return { prevDetail, prevLists };
    },

    onError(_err, vars, ctx) {
      if (!ctx) return;

      if (ctx.prevDetail) {
        qc.setQueryData<AdminBookingResult>(AdminBookingKeys.byId(vars.id), ctx.prevDetail);
      }
      for (const snap of ctx.prevLists ?? []) {
        qc.setQueryData(snap.key, snap.data);
      }
    },

    onSuccess(result, { id }) {
      if (result?.booking) {
        qc.setQueryData<AdminBookingResult>(AdminBookingKeys.byId(id), result.booking);
        patchListsBooking(qc, id, () => result.booking);
      }
    },

    onSettled(_res, _err, { id }) {
      qc.invalidateQueries({ queryKey: AdminBookingKeys.byId(id) });
      qc.invalidateQueries({ queryKey: AdminBookingKeys.all });
    },
  });
}

/** ✅ Admin: Generate Voucher */
export function useAdminGenerateVoucherMutation() {
  const qc = useQueryClient();

  return useMutation<
    AdminGenerateVoucherResult,
    unknown,
    { id: number | string; data?: AdminGenerateVoucherBody },
    UpdateAdminBookingContext
  >({
    mutationFn: ({ id, data }) => adminGenerateVoucher(id, data ?? {}),

    async onMutate({ id }) {
      await Promise.all([
        qc.cancelQueries({ queryKey: AdminBookingKeys.byId(id) }),
        qc.cancelQueries({ queryKey: AdminBookingKeys.all }),
      ]);

      const prevDetail = qc.getQueryData<AdminBookingResult>(AdminBookingKeys.byId(id));

      const prevLists: AdminBookingsListSnapshot[] = qc
        .getQueryCache()
        .findAll({ queryKey: AdminBookingKeys.all })
        .map((q) => ({
          key: q.queryKey,
          data: q.state.data as PaginatedAdminBookings | undefined,
        }));

      return { prevDetail, prevLists };
    },

    onError(_err, vars, ctx) {
      if (!ctx) return;

      if (ctx.prevDetail) {
        qc.setQueryData<AdminBookingResult>(AdminBookingKeys.byId(vars.id), ctx.prevDetail);
      }
      for (const snap of ctx.prevLists ?? []) {
        qc.setQueryData(snap.key, snap.data);
      }
    },

    onSuccess(result, { id }) {
      const snap = {
        id: result.voucherId,
        number: result.voucherNumber,
        version: result.version,
        url: result.url,
        key: result.key,
        vendorRefNumber: (result as any).vendorRefNumber ?? null,
        generatedAt: new Date().toISOString(),
      };

      const prev = qc.getQueryData<AdminBookingResult>(AdminBookingKeys.byId(id));
      if (prev) {
        qc.setQueryData<AdminBookingResult>(AdminBookingKeys.byId(id), {
          ...prev,
          voucher: snap as any,
        });
      }

      patchListsBooking(qc, id, (b) => ({
        ...b,
        voucher: snap as any,
      }));
    },

    onSettled(_res, _err, { id }) {
      qc.invalidateQueries({ queryKey: AdminBookingKeys.byId(id) });
      qc.invalidateQueries({ queryKey: AdminBookingKeys.all });
    },
  });
}


/** 🔹 Admin: Delete booking */
export function useAdminDeleteBookingMutation() {
  const qc = useQueryClient();

  return useMutation<
    { message?: string } | AdminBookingResult | null,
    unknown,
    { id: number | string; reason: string },
    DeleteAdminBookingContext
  >({
    mutationFn: ({ id, reason }) => adminDeleteBooking(id, reason),

    async onMutate({ id }) {
      await qc.cancelQueries({ queryKey: AdminBookingKeys.all });

      const prevLists: AdminBookingsListSnapshot[] = qc
        .getQueryCache()
        .findAll({ queryKey: AdminBookingKeys.all })
        .map((q) => ({
          key: q.queryKey,
          data: q.state.data as PaginatedAdminBookings | undefined,
        }));

      for (const snap of prevLists) {
        const list = snap.data;
        if (!list?.items) continue;

        const nextItems = list.items.filter((b) => String(b.id) !== String(id));
        const nextTotal = Math.max(0, Number(list.total ?? list.items.length) - 1);

        qc.setQueryData<PaginatedAdminBookings>(snap.key, {
          ...list,
          items: nextItems,
          total: nextTotal,
          totalPages:
            list.totalPages != null
              ? Math.max(1, Number(list.totalPages))
              : Math.max(1, Math.ceil(nextTotal / Math.max(1, Number(list.perPage ?? 10)))),
        });
      }

      qc.removeQueries({ queryKey: AdminBookingKeys.byId(id) });

      return { prevLists };
    },

    onError(_err, _id, ctx) {
      for (const snap of ctx?.prevLists ?? []) {
        qc.setQueryData(snap.key, snap.data);
      }
    },

    onSettled() {
      qc.invalidateQueries({ queryKey: AdminBookingKeys.all });
    },
  });
}

/**
 * 🔹 Admin: Bulk delete bookings
 *
 * No optimistic removal here. The server decides per booking whether the delete
 * is allowed, so removing rows up front would show bookings vanishing that are
 * about to come back. We refetch once the real result is known.
 */
export function useAdminBulkDeleteBookingsMutation() {
  const qc = useQueryClient();

  return useMutation<
    AdminBulkDeleteResult,
    unknown,
    { bookingIds: number[]; reason: string }
  >({
    mutationFn: ({ bookingIds, reason }) =>
      adminBulkDeleteBookings(bookingIds, reason),

    onSettled(res) {
      for (const id of res?.deleted ?? []) {
        qc.removeQueries({ queryKey: AdminBookingKeys.byId(id) });
      }
      qc.invalidateQueries({ queryKey: AdminBookingKeys.all });
    },
  });
}

/* =====================================
 * Admin Travel Plan mutations
 * ===================================== */

/** 🔹 Admin: Upsert travel plan */
export function useAdminUpsertTravelPlanMutation() {
  const qc = useQueryClient();

  return useMutation<
    AdminTravelPlanResult,
    unknown,
    { bookingId: number | string; data: AdminUpsertTravelPlanBody },
    UpdateAdminTravelPlanContext
  >({
    mutationFn: ({ bookingId, data }) => adminUpsertTravelPlan(bookingId, data),

    async onMutate({ bookingId, data }) {
      await qc.cancelQueries({ queryKey: AdminBookingKeys.travelPlan(bookingId) });

      const prevPlan = qc.getQueryData<AdminTravelPlanResult>(AdminBookingKeys.travelPlan(bookingId));

      if (prevPlan) {
        const hasSegments = Object.prototype.hasOwnProperty.call(data, "segmentsJson");
        const next: AdminTravelPlanResult = {
          ...prevPlan,
          ...(data as any),
          segmentsJson: hasSegments
            ? Array.isArray((data as any).segmentsJson)
              ? (data as any).segmentsJson
              : []
            : prevPlan.segmentsJson ?? [],
        };

        qc.setQueryData<AdminTravelPlanResult>(AdminBookingKeys.travelPlan(bookingId), next);
      }

      return { prevPlan };
    },

    onError(_err, vars, ctx) {
      if (ctx?.prevPlan) {
        qc.setQueryData<AdminTravelPlanResult>(AdminBookingKeys.travelPlan(vars.bookingId), ctx.prevPlan);
      }
    },

    onSuccess(updated, { bookingId }) {
      qc.setQueryData<AdminTravelPlanResult>(AdminBookingKeys.travelPlan(bookingId), updated);
    },

    onSettled(_res, _err, { bookingId }) {
      qc.invalidateQueries({ queryKey: AdminBookingKeys.travelPlan(bookingId) });
    },
  });
}

/** 🔹 Admin: Delete travel plan (soft delete) */
export function useAdminDeleteTravelPlanMutation() {
  const qc = useQueryClient();

  return useMutation<
    { message?: string } | AdminTravelPlanResult | null,
    unknown,
    number | string,
    DeleteAdminTravelPlanContext
  >({
    mutationFn: (bookingId) => adminDeleteTravelPlan(bookingId),

    async onMutate(bookingId) {
      await qc.cancelQueries({ queryKey: AdminBookingKeys.travelPlan(bookingId) });

      const prevPlan = qc.getQueryData<AdminTravelPlanResult>(AdminBookingKeys.travelPlan(bookingId));

      qc.removeQueries({ queryKey: AdminBookingKeys.travelPlan(bookingId) });

      return { prevPlan };
    },

    onError(_err, bookingId, ctx) {
      if (ctx?.prevPlan) {
        qc.setQueryData<AdminTravelPlanResult>(AdminBookingKeys.travelPlan(bookingId), ctx.prevPlan);
      }
    },

    onSettled(_res, _err, bookingId) {
      qc.invalidateQueries({ queryKey: AdminBookingKeys.travelPlan(bookingId) });
    },
  });
}

/**
 * 🔁 Backwards compat wrapper: (you had both hooks)
 * Keep it, but just alias to the main one.
 */
export function useAdminGenerateHdfcPaymentLinkMutation() {
  return useAdminGeneratePaymentLinkMutation();
}